import {
  Dimension,
  DimensionSet,
  IMetrics,
  MetricName,
  Operation,
} from '@amzn/work-spaces-web-client-core-lib';
import type { Metric } from 'web-vitals';

/**
 * sessionStorage key marking that web vitals were already registered for this
 * browser session. A customer session spans several full page loads
 * (registration -> login -> external auth redirect -> stream), and each load
 * would otherwise register and publish its own set of vitals.
 */
export const WEB_VITALS_REGISTERED_KEY = 'WORKSPACES_WEB_VITALS_REGISTERED';

// Fallback guard for the current page load when sessionStorage is unavailable
// (e.g. blocked by privacy settings).
let registeredThisPageLoad = false;

// Test hook: clears the page-load guard (sessionStorage is reset separately).
export const resetWebVitalsRegistrationForTesting = (): void => {
  registeredThisPageLoad = false;
};

const hasAlreadyRegistered = (): boolean => {
  if (registeredThisPageLoad) {
    return true;
  }
  try {
    return sessionStorage.getItem(WEB_VITALS_REGISTERED_KEY) === 'true';
  } catch {
    return false;
  }
};

const markRegistered = (): void => {
  registeredThisPageLoad = true;
  try {
    sessionStorage.setItem(WEB_VITALS_REGISTERED_KEY, 'true');
  } catch {
    // sessionStorage unavailable; the module flag above still guards this
    // page load.
  }
};

/**
 * Maps a web-vitals metric identifier to the published MetricName. FID is
 * intentionally omitted — it is deprecated in web-vitals v4 in favour of INP
 * and is not collected below.
 */
const METRIC_NAME_BY_VITAL: Partial<Record<Metric['name'], MetricName>> = {
  LCP: MetricName.WebVitalLCP,
  CLS: MetricName.WebVitalCLS,
  INP: MetricName.WebVitalINP,
  FCP: MetricName.WebVitalFCP,
  TTFB: MetricName.WebVitalTTFB,
};

/**
 * emitWithValue takes an integer value. Most vitals are already millisecond
 * timings; CLS is a small unitless ratio (e.g. 0.08), so scale it by 1000 to
 * retain precision once rounded.
 */
const toMetricValue = (metric: Metric): number =>
  metric.name === 'CLS'
    ? Math.round(metric.value * 1000)
    : Math.round(metric.value);

/**
 * Publishes Core Web Vitals (LCP, CLS, INP, FCP, TTFB) to the metrics endpoint
 * under Operation.WebVitals. The rating (good | needs-improvement | poor) is
 * attached as a dimension for slicing in dashboards.
 *
 * Call this AFTER registration is complete (region is known) so the metrics
 * pipeline stamps the correct Marketplace dimension and publish endpoint —
 * the web-vitals library records vitals from buffered PerformanceObserver
 * entries, so values measured before this call are still captured.
 *
 * Publishes at most once per browser session (sessionStorage-guarded): a
 * session spans several full page loads (registration -> login -> auth ->
 * stream), and only the first load's vitals are representative of the
 * customer-perceived initial page experience.
 */
const reportWebVitals = (metrics?: IMetrics): void => {
  if (!metrics || hasAlreadyRegistered()) {
    return;
  }
  markRegistered();

  const publish = (metric: Metric): void => {
    const name = METRIC_NAME_BY_VITAL[metric.name];
    if (!name) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const dimensions = {} as DimensionSet;
    dimensions[Dimension.Rating] = metric.rating;
    metrics.emitWithValue(
      Operation.WebVitals,
      name,
      toMetricValue(metric),
      dimensions
    );
  };

  import('web-vitals').then(({ onCLS, onINP, onFCP, onLCP, onTTFB }) => {
    onCLS(publish);
    onINP(publish);
    onFCP(publish);
    onLCP(publish);
    onTTFB(publish);
  });
};

export default reportWebVitals;
